import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  getQuotations,
  saveQuotation,
  deleteQuotation,
  updateQuotationStatus,
  convertQuotationToOrder,
  generateQuotationNumber,
  getCustomers,
  getProducts,
  Quotation,
  QuotationItem,
  Customer,
  Product,
} from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { QuotationDocument } from '@/components/invoice/QuotationDocument'
import { useReactToPrint } from 'react-to-print'
import {
  FileText,
  Plus,
  Search,
  Printer,
  Trash2,
  Edit3,
  Copy,
  ArrowRightCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  Check,
  X,
  Building2,
  Package,
  DollarSign,
  Eye,
  RefreshCw,
  Sparkles,
  ClipboardList,
} from 'lucide-react'

export const Route = createFileRoute('/admin/quotations')({
  component: QuotationsPage,
})

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: any }> = {
  draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-700', icon: Clock },
  sent: { label: 'Sent', bg: 'bg-blue-100', text: 'text-blue-700', icon: Send },
  accepted: { label: 'Accepted', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', bg: 'bg-rose-100', text: 'text-rose-700', icon: XCircle },
  converted: { label: 'Converted to Sale', bg: 'bg-purple-100', text: 'text-purple-700', icon: ArrowRightCircle },
}

const DEFAULT_TERMS_PRESETS = [
  '1. Prices are valid for 15 days from the date of quotation.\n2. Payment terms: 100% advance or approved credit terms.\n3. Delivery timeframe: 3 to 7 working days from formal purchase order.\n4. Standard warranty applies as per manufacturer terms.',
  '1. Quotation valid for 30 days.\n2. 50% advance with Purchase Order, 50% upon delivery.\n3. Taxes are charged as applicable under Federal/Provincial sales tax laws.\n4. Ex-Factory delivery Lahore.',
  '1. Quotation based on ready stock availability.\n2. Goods once sold will not be taken back without prior authorization.\n3. Payment strictly within 15 days of invoice date.',
]

const SUBJECT_PRESETS = [
  'Quotation for Hardware Supplies & Material',
  'Quotation for Machinery & Loom Spare Parts',
  'Supply of Industrial Tools & Mechanical Hardware',
  'Quotation for Electrical & Pneumatic Equipment',
  'Rate Offer for High Tensile Fasteners & Fixtures',
]

function QuotationsPage() {
  const { user } = useAuth()

  // Data
  const [quotations, setQuotations] = useState<Quotation[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  // Filters & State
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [printData, setPrintData] = useState<Quotation | null>(null)
  const [previewData, setPreviewData] = useState<Quotation | null>(null)

  // Modal State
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [convertingId, setConvertingId] = useState<string | null>(null)

  // Form State
  const [form, setForm] = useState<Partial<Quotation>>({
    quotationNumber: generateQuotationNumber(),
    date: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
    customerId: '',
    customerName: '',
    companyName: '',
    companyAddress: '',
    customerPhone: '',
    customerEmail: '',
    customerNtn: '',
    customerStn: '',
    attnName1: '',
    attnRole1: '',
    attnName2: '',
    attnRole2: '',
    subject: 'Quotation for Hardware Supplies & Material',
    items: [],
    subtotal: 0,
    taxRate: 18,
    taxAmount: 0,
    discountAmount: 0,
    totalAmount: 0,
    status: 'draft',
    termsAndConditions: DEFAULT_TERMS_PRESETS[0],
    notes: '',
  })

  // Item Form State (for adding line items)
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [itemForm, setItemForm] = useState<QuotationItem>({
    sNo: 1,
    productId: '',
    description: '',
    brand: '',
    itemCode: '',
    hsCode: '',
    unitRate: '',
    quantity: 1,
    uom: 'PCS',
    taxRate: 18,
    discount: 0,
    total: '',
  })

  // React-to-print setup
  const printRef = useRef<HTMLDivElement>(null)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Quotation_${printData?.quotationNumber || 'Doc'}`,
    onAfterPrint: () => setPrintData(null),
  })

  useEffect(() => {
    if (printData && printRef.current) {
      handlePrint()
    }
  }, [printData, handlePrint])

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      const [qData, cData, pData] = await Promise.all([
        getQuotations(),
        getCustomers(),
        getProducts(),
      ])
      setQuotations(qData)
      setCustomers(cData)
      setProducts(pData)
    } catch (err: any) {
      console.error(err)
      alert(friendlyError(err, 'Failed to load quotations'))
    } finally {
      setLoading(false)
    }
  }

  // Format currency
  const f = (n: number | string | undefined) => {
    if (n === undefined || n === null || n === '') return '—'
    if (typeof n === 'string' && isNaN(Number(n))) return n
    const num = typeof n === 'string' ? parseFloat(n) : n
    return num.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Filtered Quotations
  const filteredQuotations = useMemo(() => {
    return quotations.filter((q) => {
      const matchesSearch =
        !search ||
        q.quotationNumber?.toLowerCase().includes(search.toLowerCase()) ||
        q.companyName?.toLowerCase().includes(search.toLowerCase()) ||
        q.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        q.subject?.toLowerCase().includes(search.toLowerCase()) ||
        q.attnName1?.toLowerCase().includes(search.toLowerCase())

      const matchesStatus = statusFilter === 'all' || q.status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [quotations, search, statusFilter])

  // Top Metrics
  const metrics = useMemo(() => {
    const total = quotations.length
    const draftCount = quotations.filter((q) => q.status === 'draft' || q.status === 'sent').length
    const convertedCount = quotations.filter((q) => q.status === 'converted' || q.status === 'accepted').length
    const totalValue = quotations.reduce((acc, q) => acc + (q.totalAmount || 0), 0)
    const convertedValue = quotations
      .filter((q) => q.status === 'converted' || q.status === 'accepted')
      .reduce((acc, q) => acc + (q.totalAmount || 0), 0)

    return { total, draftCount, convertedCount, totalValue, convertedValue }
  }, [quotations])

  // Open New Quotation Modal
  const openNew = () => {
    setIsEditing(false)
    setForm({
      quotationNumber: generateQuotationNumber(),
      date: new Date().toISOString().split('T')[0],
      validUntil: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
      customerId: '',
      customerName: '',
      companyName: '',
      companyAddress: '',
      customerPhone: '',
      customerEmail: '',
      customerNtn: '',
      customerStn: '',
      attnName1: '',
      attnRole1: '',
      attnName2: '',
      attnRole2: '',
      subject: SUBJECT_PRESETS[0],
      items: [],
      subtotal: 0,
      taxRate: 18,
      taxAmount: 0,
      discountAmount: 0,
      totalAmount: 0,
      status: 'draft',
      termsAndConditions: DEFAULT_TERMS_PRESETS[0],
      notes: '',
    })
    resetItemForm(1)
    setShowModal(true)
  }

  // Open Edit Modal
  const openEdit = (q: Quotation) => {
    setIsEditing(true)
    setForm({ ...q })
    resetItemForm((q.items?.length || 0) + 1)
    setShowModal(true)
  }

  // Duplicate / Clone Quotation
  const handleDuplicate = (q: Quotation) => {
    setIsEditing(false)
    setForm({
      ...q,
      id: undefined,
      quotationNumber: generateQuotationNumber(),
      date: new Date().toISOString().split('T')[0],
      validUntil: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
      status: 'draft',
      convertedOrderId: undefined,
    })
    resetItemForm((q.items?.length || 0) + 1)
    setShowModal(true)
  }

  // Reset Line Item Form
  const resetItemForm = (nextIndex: number) => {
    setSelectedProductId('')
    setItemForm({
      sNo: nextIndex,
      productId: '',
      description: '',
      brand: '',
      itemCode: '',
      hsCode: '',
      unitRate: '',
      quantity: 1,
      uom: 'PCS',
      taxRate: 18,
      discount: 0,
      total: '',
    })
  }

  // Select Registered Customer
  const handleSelectCustomer = (customerId: string) => {
    if (!customerId) {
      setForm((prev) => ({
        ...prev,
        customerId: '',
        customerName: '',
        companyName: '',
        companyAddress: '',
        customerPhone: '',
        customerEmail: '',
        customerNtn: '',
        customerStn: '',
      }))
      return
    }

    const c = customers.find((cust) => cust.id === customerId)
    if (c) {
      setForm((prev) => ({
        ...prev,
        customerId: c.id,
        customerName: c.name,
        companyName: c.name,
        companyAddress: c.address ? `${c.address}${c.city ? `, ${c.city}` : ''}` : (c.city || ''),
        customerPhone: c.phone || '',
        customerEmail: c.email || '',
        customerNtn: c.ntn || '',
        customerStn: c.stn || '',
      }))
    }
  }

  // Select Catalog Product
  const handleSelectProduct = (productId: string) => {
    setSelectedProductId(productId)
    if (!productId) return

    const p = products.find((prod) => prod.id === productId)
    if (p) {
      const price = p.price || 0
      const qty = itemForm.quantity || 1
      const total = price * qty

      setItemForm((prev) => ({
        ...prev,
        productId: p.id,
        description: p.name,
        brand: p.brand || '',
        itemCode: p.code || '',
        hsCode: p.hsCode || '',
        unitRate: price,
        uom: p.unit || 'PCS',
        taxRate: p.taxRate ?? 18,
        total: total,
      }))
    }
  }

  // Recalculate Row Total
  const handleItemFieldChange = (field: keyof QuotationItem, val: any) => {
    setItemForm((prev) => {
      const updated = { ...prev, [field]: val }
      if (field === 'quantity' || field === 'unitRate' || field === 'discount') {
        const qty = Number(field === 'quantity' ? val : updated.quantity) || 0
        const rate = Number(field === 'unitRate' ? val : updated.unitRate)
        const disc = Number(field === 'discount' ? val : updated.discount) || 0

        if (!isNaN(rate) && !isNaN(qty)) {
          const lineVal = Math.max(0, rate * qty - disc)
          updated.total = Math.round(lineVal * 100) / 100
        }
      }
      return updated
    })
  }

  // Add Item to Grid
  const addItemToGrid = () => {
    if (!itemForm.description.trim()) {
      alert('Please enter an item description or select a product.')
      return
    }

    const nextItems = [...(form.items || [])]
    const itemToAdd: QuotationItem = {
      ...itemForm,
      sNo: nextItems.length + 1,
      total:
        itemForm.total !== ''
          ? itemForm.total
          : typeof itemForm.unitRate === 'number'
            ? Number(itemForm.unitRate) * Number(itemForm.quantity)
            : itemForm.unitRate,
    }

    nextItems.push(itemToAdd)
    recalculateTotals(nextItems, form.discountAmount || 0, form.taxRate ?? 18)
    resetItemForm(nextItems.length + 1)
  }

  // Remove Item from Grid
  const removeItem = (index: number) => {
    const nextItems = (form.items || [])
      .filter((_, i) => i !== index)
      .map((it, idx) => ({ ...it, sNo: idx + 1 }))

    recalculateTotals(nextItems, form.discountAmount || 0, form.taxRate ?? 18)
    resetItemForm(nextItems.length + 1)
  }

  // Recalculate Financial Summary
  const recalculateTotals = (items: QuotationItem[], discount: number, taxRate: number) => {
    let subtotal = 0
    items.forEach((it) => {
      const t = typeof it.total === 'number' ? it.total : parseFloat(String(it.total))
      if (!isNaN(t)) subtotal += t
    })

    const discountedSubtotal = Math.max(0, subtotal - discount)
    const taxAmount = (discountedSubtotal * taxRate) / 100
    const grandTotal = discountedSubtotal + taxAmount

    setForm((prev) => ({
      ...prev,
      items,
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: discount,
      taxRate: taxRate,
      taxAmount: Math.round(taxAmount * 100) / 100,
      totalAmount: Math.round(grandTotal * 100) / 100,
    }))
  }

  // Save Quotation
  const handleSave = async (andPrint = false) => {
    if (!form.companyName?.trim()) {
      alert('Company/Customer name is required.')
      return
    }
    if (!form.subject?.trim()) {
      alert('Quotation subject is required.')
      return
    }
    if (!form.items || form.items.length === 0) {
      alert('Please add at least one item to the quotation grid.')
      return
    }

    setSaving(true)
    try {
      const payload: Quotation = {
        id: form.id,
        quotationNumber: form.quotationNumber || generateQuotationNumber(),
        date: form.date || new Date().toISOString().split('T')[0],
        validUntil: form.validUntil || '',
        customerId: form.customerId || '',
        customerName: form.customerName || form.companyName || '',
        companyName: form.companyName,
        companyAddress: form.companyAddress || '',
        customerPhone: form.customerPhone || '',
        customerEmail: form.customerEmail || '',
        customerNtn: form.customerNtn || '',
        customerStn: form.customerStn || '',
        attnName1: form.attnName1 || '',
        attnRole1: form.attnRole1 || '',
        attnName2: form.attnName2 || '',
        attnRole2: form.attnRole2 || '',
        subject: form.subject,
        items: form.items,
        subtotal: form.subtotal || 0,
        taxRate: form.taxRate ?? 18,
        taxAmount: form.taxAmount || 0,
        discountAmount: form.discountAmount || 0,
        totalAmount: form.totalAmount || 0,
        status: form.status || 'draft',
        termsAndConditions: form.termsAndConditions || '',
        notes: form.notes || '',
        createdBy: user?.email || 'admin',
      }

      await saveQuotation(payload)
      setShowModal(false)
      await loadAll()

      if (andPrint) {
        setPrintData(payload)
      }
    } catch (err: any) {
      console.error(err)
      alert(friendlyError(err, 'Failed to save quotation'))
    } finally {
      setSaving(false)
    }
  }

  // Quick Status Update
  const handleStatusChange = async (id: string, newStatus: Quotation['status']) => {
    try {
      await updateQuotationStatus(id, newStatus)
      setQuotations((prev) => prev.map((q) => (q.id === id ? { ...q, status: newStatus } : q)))
    } catch (err: any) {
      alert(friendlyError(err, 'Could not update status'))
    }
  }

  // Convert Quotation to POS Sale/Order
  const handleConvertToOrder = async (q: Quotation) => {
    if (q.status === 'converted') {
      alert('This quotation has already been converted to an order.')
      return
    }

    if (
      !confirm(
        `Convert Quotation #${q.quotationNumber} (${q.companyName}) into an active POS Sale / Order of PKR ${f(q.totalAmount)}?`
      )
    ) {
      return
    }

    setConvertingId(q.id || '')
    try {
      const orderId = await convertQuotationToOrder(q, user?.email || 'admin')
      alert(`🎉 Quotation converted successfully!\nCreated Order ID: ${orderId}`)
      await loadAll()
    } catch (err: any) {
      console.error(err)
      alert(friendlyError(err, 'Could not convert quotation to order'))
    } finally {
      setConvertingId(null)
    }
  }

  // Delete Quotation
  const handleDelete = async (id: string | undefined) => {
    if (!id || !confirm('Are you sure you want to delete this quotation permanently?')) return
    try {
      await deleteQuotation(id)
      setQuotations((prev) => prev.filter((q) => q.id !== id))
    } catch (err: any) {
      alert(friendlyError(err, 'Could not delete quotation'))
    }
  }

  if (loading) {
    return (
      <div className="p-4 pt-14 lg:pt-5 lg:p-5 bg-[#f0f5ff] min-h-screen animate-pulse">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-60 bg-slate-200 rounded-lg" />
          <div className="h-10 w-36 bg-slate-200 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-white rounded-2xl border border-slate-200/60" />
          ))}
        </div>
        <div className="bg-white rounded-2xl h-96 border border-slate-200/60" />
      </div>
    )
  }

  return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-6 bg-[#f0f5ff] min-h-screen font-sans">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-black text-slate-800 flex items-center gap-2.5">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            Quotations &amp; Estimates
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Create, manage, track, print, and convert commercial customer quotes
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            className="p-2.5 bg-white hover:bg-slate-50 text-slate-600 rounded-xl border border-slate-200/80 shadow-sm transition-all"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-lg shadow-blue-500/25 transition-all transform active:scale-95"
          >
            <Plus className="w-4 h-4" />
            New Quotation
          </button>
        </div>
      </div>

      {/* KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200/70 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Quotes</span>
            <div className="text-2xl font-black text-slate-800 mt-1">{metrics.total}</div>
            <span className="text-[11px] text-slate-500 mt-0.5 block">Lifetime recorded</span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600">
            <FileText className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200/70 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-amber-500 uppercase tracking-wider">Draft / Sent</span>
            <div className="text-2xl font-black text-slate-800 mt-1">{metrics.draftCount}</div>
            <span className="text-[11px] text-amber-600 mt-0.5 block font-medium">Pending response</span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
            <Clock className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200/70 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-purple-500 uppercase tracking-wider">Converted Sales</span>
            <div className="text-2xl font-black text-slate-800 mt-1">{metrics.convertedCount}</div>
            <span className="text-[11px] text-purple-600 mt-0.5 block font-medium">Transformed into orders</span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-600">
            <ArrowRightCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200/70 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-emerald-500 uppercase tracking-wider">Quoted Pipeline</span>
            <div className="text-xl font-black text-emerald-700 mt-1 font-mono">PKR {f(metrics.totalValue)}</div>
            <span className="text-[11px] text-emerald-600 mt-0.5 block font-medium">
              Won: PKR {f(metrics.convertedValue)}
            </span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
            <DollarSign className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Search & Status Filters */}
      <div className="bg-white rounded-2xl border border-slate-200/70 p-4 mb-4 shadow-sm space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by quote #, company, customer, attention, or subject..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 bg-slate-50/60 transition-all font-medium"
            />
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
            {[
              { key: 'all', label: 'All Quotes' },
              { key: 'draft', label: 'Draft' },
              { key: 'sent', label: 'Sent' },
              { key: 'accepted', label: 'Accepted' },
              { key: 'converted', label: 'Converted' },
              { key: 'rejected', label: 'Rejected' },
            ].map((tab) => {
              const active = statusFilter === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                    active
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Main Quotations Table */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                <th className="px-4 py-3">Quote #</th>
                <th className="px-4 py-3">Date / Valid Until</th>
                <th className="px-4 py-3">Company / Customer</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3 text-center">Items</th>
                <th className="px-4 py-3 text-right">Total (PKR)</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredQuotations.map((q) => {
                const statusMeta = STATUS_CONFIG[q.status] || STATUS_CONFIG.draft
                const StatusIcon = statusMeta.icon

                return (
                  <tr key={q.id} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-4 py-3 font-mono font-bold text-blue-600">
                      {q.quotationNumber}
                      {q.convertedOrderId && (
                        <span className="block text-[9px] text-purple-600 font-sans font-semibold">
                          Order Linked ✓
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{q.date}</div>
                      {q.validUntil && (
                        <div className="text-[10px] text-slate-400">Valid: {q.validUntil}</div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-800">{q.companyName}</div>
                      {q.attnName1 && (
                        <div className="text-[10px] text-slate-500">
                          Attn: {q.attnName1} {q.attnRole1 ? `(${q.attnRole1})` : ''}
                        </div>
                      )}
                      {q.customerPhone && (
                        <div className="text-[10px] text-slate-400">📞 {q.customerPhone}</div>
                      )}
                    </td>

                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate font-medium" title={q.subject}>
                      {q.subject}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-slate-100 font-bold text-[10px] text-slate-600">
                        {q.items?.length || 0}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right font-mono font-black text-slate-900 text-[13px]">
                      {f(q.totalAmount)}
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${statusMeta.bg} ${statusMeta.text}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {statusMeta.label}
                        </span>

                        {/* Quick Status Changer dropdown */}
                        <select
                          value={q.status}
                          onChange={(e) => handleStatusChange(q.id!, e.target.value as any)}
                          className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-600 focus:outline-none focus:border-blue-500"
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="accepted">Accepted</option>
                          <option value="rejected">Rejected</option>
                          {q.status === 'converted' && <option value="converted">Converted</option>}
                        </select>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* Print / Preview */}
                        <button
                          onClick={() => setPrintData(q)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Print Document"
                        >
                          <Printer className="w-4 h-4" />
                        </button>

                        {/* Preview Modal */}
                        <button
                          onClick={() => setPreviewData(q)}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                          title="Preview Layout"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Convert to Sale */}
                        {q.status !== 'converted' ? (
                          <button
                            onClick={() => handleConvertToOrder(q)}
                            disabled={convertingId === q.id}
                            className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all disabled:opacity-50"
                            title="Convert into POS Order"
                          >
                            <ArrowRightCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="p-1.5 text-purple-400" title="Already Converted">
                            <Check className="w-4 h-4" />
                          </span>
                        )}

                        {/* Duplicate */}
                        <button
                          onClick={() => handleDuplicate(q)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                          title="Duplicate Quotation"
                        >
                          <Copy className="w-4 h-4" />
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => openEdit(q)}
                          className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                          title="Edit Details"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(q.id)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          title="Delete Quotation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}

              {filteredQuotations.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-slate-400">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30 text-blue-500" />
                    <p className="text-sm font-bold text-slate-600">No quotations found</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {search || statusFilter !== 'all'
                        ? 'Try changing your search keywords or status filter.'
                        : 'Create your first commercial quotation to get started.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden Print Container */}
      <div style={{ display: 'none' }}>
        {printData && <QuotationDocument ref={printRef} data={printData} />}
      </div>

      {/* Document Layout Preview Modal */}
      {previewData && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h3 className="font-black text-sm text-slate-800">
                  Quotation Preview #{previewData.quotationNumber}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setPrintData(previewData)
                    setPreviewData(null)
                  }}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-xl text-xs font-bold transition-all"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Print A4
                </button>
                <button
                  onClick={() => setPreviewData(null)}
                  className="p-1.5 text-slate-400 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto bg-slate-100 flex justify-center">
              <div className="shadow-lg border border-slate-300 bg-white">
                <QuotationDocument data={previewData} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rich Create / Edit Quotation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] shadow-2xl flex flex-col overflow-hidden my-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800">
                    {isEditing ? 'Edit Quotation' : 'Create Commercial Quotation'}
                  </h3>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Configure customer details, specifications grid, tax breakdown, and terms
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:bg-slate-200 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 text-xs">
              {/* Top Meta Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-slate-50/80 rounded-2xl border border-slate-200/80">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Quote Number *
                  </label>
                  <input
                    value={form.quotationNumber || ''}
                    onChange={(e) => setForm({ ...form, quotationNumber: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono font-bold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Quotation Date *
                  </label>
                  <input
                    type="date"
                    value={form.date || ''}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Valid Until Date
                  </label>
                  <input
                    type="date"
                    value={form.validUntil || ''}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Status
                  </label>
                  <select
                    value={form.status || 'draft'}
                    onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent to Client</option>
                    <option value="accepted">Accepted by Client</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Customer Selection & Details */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-2">
                  <span className="font-black text-slate-800 text-xs flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Customer &amp; Client Information
                  </span>

                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-medium">Quick pick customer:</span>
                    <select
                      value={form.customerId || ''}
                      onChange={(e) => handleSelectCustomer(e.target.value)}
                      className="px-3 py-1.5 rounded-xl border border-blue-200 text-xs font-bold text-blue-700 bg-blue-50/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">— Select Registered Client —</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.phone ? `(${c.phone})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Company / Buyer Name *
                    </label>
                    <input
                      placeholder="e.g. Sarena Textile Industries"
                      value={form.companyName || ''}
                      onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 font-bold text-slate-800 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Company Address / City
                    </label>
                    <input
                      placeholder="e.g. Sheikhupura Road, Lahore"
                      value={form.companyAddress || ''}
                      onChange={(e) => setForm({ ...form, companyAddress: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Contact Phone
                    </label>
                    <input
                      placeholder="e.g. +92 300 1234567"
                      value={form.customerPhone || ''}
                      onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Customer Email
                    </label>
                    <input
                      placeholder="e.g. procurement@sarena.com"
                      value={form.customerEmail || ''}
                      onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Customer NTN
                    </label>
                    <input
                      placeholder="e.g. 1234567-8"
                      value={form.customerNtn || ''}
                      onChange={(e) => setForm({ ...form, customerNtn: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Customer STN
                    </label>
                    <input
                      placeholder="e.g. 12-34-5678-901-23"
                      value={form.customerStn || ''}
                      onChange={(e) => setForm({ ...form, customerStn: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Attention Block */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="text-[10px] font-bold uppercase text-blue-600 tracking-wider block mb-1">
                      Primary Attention Line
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        placeholder="Name (e.g. Tariq Waseem)"
                        value={form.attnName1 || ''}
                        onChange={(e) => setForm({ ...form, attnName1: e.target.value })}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
                      />
                      <input
                        placeholder="Role (e.g. Purchase Manager)"
                        value={form.attnRole1 || ''}
                        onChange={(e) => setForm({ ...form, attnRole1: e.target.value })}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase text-blue-600 tracking-wider block mb-1">
                      Secondary Attention Line (Optional)
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        placeholder="Name (e.g. Mr. Shoaib)"
                        value={form.attnName2 || ''}
                        onChange={(e) => setForm({ ...form, attnName2: e.target.value })}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
                      />
                      <input
                        placeholder="Role (e.g. Purchase Officer)"
                        value={form.attnRole2 || ''}
                        onChange={(e) => setForm({ ...form, attnRole2: e.target.value })}
                        className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Subject Header */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Subject Line *
                    </label>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-400">Presets:</span>
                      <select
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        className="text-[10px] border border-slate-200 rounded px-2 py-0.5 bg-slate-50 text-slate-600"
                      >
                        <option value="">Choose preset...</option>
                        {SUBJECT_PRESETS.map((p, idx) => (
                          <option key={idx} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <input
                    value={form.subject || ''}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 font-bold text-slate-800 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    placeholder="Enter document subject"
                  />
                </div>
              </div>

              {/* Items Table & Line Item Builder */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-sm space-y-4">
                <span className="font-black text-slate-800 text-xs flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-blue-600" />
                  Item Specifications &amp; Pricing Grid
                </span>

                {/* Current Items Grid */}
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200 text-slate-500 text-[10px] uppercase font-bold">
                        <th className="px-3 py-2 w-10 text-center">S.#</th>
                        <th className="px-3 py-2">Item Detail &amp; Specifications</th>
                        <th className="px-3 py-2 text-center w-24">Brand</th>
                        <th className="px-3 py-2 text-center w-24">Code</th>
                        <th className="px-3 py-2 text-center w-16">UOM</th>
                        <th className="px-3 py-2 text-center w-16">Qty</th>
                        <th className="px-3 py-2 text-right w-24">Rate</th>
                        <th className="px-3 py-2 text-right w-28">Total</th>
                        <th className="px-3 py-2 w-10 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {form.items?.map((it, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/70">
                          <td className="px-3 py-2 text-center font-bold text-slate-400">{it.sNo}</td>
                          <td className="px-3 py-2 font-bold text-slate-800">
                            {it.description}
                            {it.hsCode && <span className="block text-[9px] text-slate-400 font-mono">HS: {it.hsCode}</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">{it.brand || '—'}</td>
                          <td className="px-3 py-2 text-center font-mono text-slate-600">{it.itemCode || '—'}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{it.uom || 'PCS'}</td>
                          <td className="px-3 py-2 text-center font-bold text-slate-800">{it.quantity}</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">{f(it.unitRate)}</td>
                          <td className="px-3 py-2 text-right font-mono font-black text-slate-900">{f(it.total)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => removeItem(idx)}
                              className="p-1 text-slate-300 hover:text-rose-600 rounded"
                              title="Remove item"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}

                      {(!form.items || form.items.length === 0) && (
                        <tr>
                          <td colSpan={9} className="py-6 text-center text-slate-400 italic">
                            No items added yet. Use the tool below to pick catalog products or add custom specifications.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Add Line Item Box */}
                <div className="p-4 bg-gradient-to-r from-blue-50/60 to-indigo-50/60 rounded-2xl border border-blue-100 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                      Add Line Item
                    </span>

                    {/* Catalog Picker */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-blue-700 font-semibold">Pick from Inventory:</span>
                      <select
                        value={selectedProductId}
                        onChange={(e) => handleSelectProduct(e.target.value)}
                        className="px-2.5 py-1 rounded-lg border border-blue-200 text-xs font-semibold text-slate-700 bg-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— Select Catalog Item —</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.code || 'No Code'}) - PKR {p.price}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Item Description &amp; Specifications *
                      </label>
                      <input
                        placeholder="e.g. S-320 Rapier Gripper Left Hand"
                        value={itemForm.description}
                        onChange={(e) => handleItemFieldChange('description', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-medium focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Brand / Make
                      </label>
                      <input
                        placeholder="e.g. Picanol / OEM"
                        value={itemForm.brand}
                        onChange={(e) => handleItemFieldChange('brand', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Item Code / Part #
                      </label>
                      <input
                        placeholder="e.g. PIC-8832"
                        value={itemForm.itemCode}
                        onChange={(e) => handleItemFieldChange('itemCode', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        HS Code
                      </label>
                      <input
                        placeholder="e.g. 8448.3900"
                        value={itemForm.hsCode}
                        onChange={(e) => handleItemFieldChange('hsCode', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Unit of Measure
                      </label>
                      <input
                        placeholder="PCS, MTR, KG, SET"
                        value={itemForm.uom}
                        onChange={(e) => handleItemFieldChange('uom', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-bold focus:ring-2 focus:ring-blue-500 bg-white uppercase text-center"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Unit Rate (PKR)
                      </label>
                      <input
                        placeholder="e.g. 1500 or NILL"
                        value={itemForm.unitRate}
                        onChange={(e) => handleItemFieldChange('unitRate', e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono font-bold focus:ring-2 focus:ring-blue-500 bg-white text-right"
                      />
                    </div>

                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={itemForm.quantity}
                        onChange={(e) => handleItemFieldChange('quantity', Number(e.target.value))}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono font-bold focus:ring-2 focus:ring-blue-500 bg-white text-center"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <div className="text-xs font-mono">
                      <span className="text-slate-500 font-sans">Line Total: </span>
                      <strong className="text-blue-700 text-sm">{f(itemForm.total)}</strong>
                    </div>

                    <button
                      onClick={addItemToGrid}
                      type="button"
                      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      Add to Quotation Grid
                    </button>
                  </div>
                </div>

                {/* Financial Summary & Totals */}
                <div className="flex flex-col sm:flex-row justify-end items-start gap-4 pt-2">
                  <div className="w-full sm:w-80 bg-slate-50 p-4 rounded-2xl border border-slate-200 space-y-2.5 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Subtotal:</span>
                      <span className="font-mono font-bold text-slate-900">{f(form.subtotal)}</span>
                    </div>

                    <div className="flex justify-between items-center gap-3">
                      <span className="text-slate-600 font-medium">Discount (PKR):</span>
                      <input
                        type="number"
                        min="0"
                        value={form.discountAmount || 0}
                        onChange={(e) => {
                          const disc = Number(e.target.value) || 0
                          recalculateTotals(form.items || [], disc, form.taxRate ?? 18)
                        }}
                        className="w-28 px-2 py-1 border border-slate-200 rounded-lg text-right font-mono font-bold bg-white text-xs text-red-600"
                      />
                    </div>

                    <div className="flex justify-between items-center gap-3">
                      <span className="text-slate-600 font-medium">Sales Tax (%):</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={form.taxRate ?? 18}
                        onChange={(e) => {
                          const rate = Number(e.target.value) || 0
                          recalculateTotals(form.items || [], form.discountAmount || 0, rate)
                        }}
                        className="w-28 px-2 py-1 border border-slate-200 rounded-lg text-right font-mono font-bold bg-white text-xs"
                      />
                    </div>

                    <div className="flex justify-between items-center text-slate-600">
                      <span>Tax Amount:</span>
                      <span className="font-mono font-bold">+{f(form.taxAmount)}</span>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t-2 border-slate-200 font-black text-sm text-slate-900">
                      <span>Total Amount:</span>
                      <span className="font-mono text-blue-700">PKR {f(form.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions & Notes */}
              <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Terms &amp; Conditions
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400">Presets:</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, termsAndConditions: DEFAULT_TERMS_PRESETS[0] })}
                      className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-[10px] font-semibold"
                    >
                      Standard (15d)
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, termsAndConditions: DEFAULT_TERMS_PRESETS[1] })}
                      className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-[10px] font-semibold"
                    >
                      50% Advance
                    </button>
                  </div>
                </div>

                <textarea
                  rows={4}
                  value={form.termsAndConditions || ''}
                  onChange={(e) => setForm({ ...form, termsAndConditions: e.target.value })}
                  placeholder="Enter business, delivery, warranty and payment terms..."
                  className="w-full p-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 font-mono leading-relaxed"
                />

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Additional Remarks / Notes
                  </label>
                  <input
                    value={form.notes || ''}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. Special packing instructions or client PO reference"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-3xl">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-md transition-all disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Quotation'}
                </button>

                <button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl text-xs font-bold shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50"
                >
                  <Printer className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save & Print'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
